/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, History, Plus, RotateCcw
} from 'lucide-react';
import Modal from '../Modal';
import { aiAgentService } from '../../services/aiAgentService';
import { getApiErrorMessage } from '../../services/knowledgeBaseService';
import toastService from '../../utils/toastService';
import { formatDateTime } from '../../utils/agenticAiDates';

const versionLabel = (v) => v?.version || v?.id || '';

// Version badges shown before "Show all".
const VISIBLE_VERSIONS = 3;

// Fixed-size box for the prompt (and its loader / error): short and long prompts take the same space.
const promptFrameClass = 'w-full h-[50vh] min-h-[16rem] bg-base-200/50 border border-base-300 rounded-xl';

// Compact modal buttons, shared by Previous / Next / Restore and the restore confirmation.
const secondaryButtonClass = 'inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-base-content/10 text-xs font-bold text-base-content/70 hover:text-primary hover:border-primary/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all';
const primaryButtonClass = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-content text-xs font-bold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all';

const ActiveChip = () => (
  <span className="px-2 py-0.5 rounded-full bg-success/10 text-success text-[10px] font-black uppercase tracking-widest">
    Active
  </span>
);

/**
 * Edit and view pages: the agent's system prompt versions. Clicking a version
 * opens its prompt read-only, with a Restore button. Restoring changes the agent
 * immediately, then hands the refreshed agent (and the restored version) to
 * `onRestored` so the page can update its prompt. The view page hides the
 * "New version" badge (`showNewVersion`) and, without update access, Restore (`canRestore`).
 */
const PromptVersionsPanel = ({
  agentId, accountParams, onRestored, disabled = false, newVersionSelected = false, onNewVersionChange,
  showNewVersion = true, canRestore = true,
}) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // The version open in the view modal: { id, version, isActive } from the list, plus `prompt` once loaded.
  const [viewing, setViewing] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [showAllVersions, setShowAllVersions] = useState(false);
  const viewRequest = useRef(0);

  const accountId = accountParams.accountId;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await aiAgentService.getPromptVersions(agentId, accountParams);
        if (!cancelled) { setVersions(Array.isArray(res.data?.data) ? res.data.data : []); }
      } catch (err) {
        if (!cancelled) { setError(getApiErrorMessage(err, 'Failed to load prompt versions')); }
      } finally {
        if (!cancelled) { setLoading(false); }
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, accountId, reloadKey]);

  const openVersion = async (version) => {
    // Only the latest request may update the modal (Previous / Next can be clicked quickly).
    const request = ++viewRequest.current;
    setViewing(version);
    setViewError(null);
    setViewLoading(true);
    try {
      const res = await aiAgentService.getPromptVersion(agentId, version.id, accountParams);
      if (request === viewRequest.current) {
        setViewing((current) => (current?.id === version.id ? { ...current, ...res.data?.data } : current));
      }
    } catch (err) {
      if (request === viewRequest.current) {
        setViewError(getApiErrorMessage(err, 'Failed to load prompt version'));
      }
    } finally {
      if (request === viewRequest.current) { setViewLoading(false); }
    }
  };

  // Position of the open version in the list (newest first): Previous is older, Next is newer.
  const viewIndex = viewing ? versions.findIndex((v) => v.id === viewing.id) : -1;
  const olderVersion = viewIndex >= 0 ? versions[viewIndex + 1] : undefined;
  const newerVersion = viewIndex > 0 ? versions[viewIndex - 1] : undefined;

  const closeView = useCallback(() => {
    if (restoring) { return; }
    setViewing(null);
    setConfirming(false);
  }, [restoring]);

  const handleRestore = async () => {
    try {
      setRestoring(true);
      const res = await aiAgentService.restorePromptVersion(agentId, viewing.id, accountParams);
      toastService.success(`System prompt restored to ${versionLabel(viewing)}`);
      const restored = viewing;
      setViewing(null);
      setConfirming(false);
      if (onRestored) { onRestored(res.data?.data, restored); }
      setReloadKey((k) => k + 1);
    } catch (err) {
      toastService.error(getApiErrorMessage(err, 'Failed to restore prompt version'));
    } finally {
      setRestoring(false);
    }
  };

  // One badge per version (the active one highlighted; click to view and restore).
  const renderVersionBadges = () => {
    if (loading) {
      return [1, 2, 3].map((i) => (
        <div key={i} className="h-7 w-16 bg-base-200/50 rounded-full animate-pulse"></div>
      ));
    }
    if (error) { return null; }
    if (versions.length === 0) {
      return <span className="text-xs text-base-content/40">No saved prompt versions yet.</span>;
    }
    // The list is newest first: only the latest few until "Show all" is clicked.
    const shown = showAllVersions ? versions : versions.slice(0, VISIBLE_VERSIONS);
    const hiddenCount = versions.length - VISIBLE_VERSIONS;
    // Same tooltip style as the list grids (e.g. Created Date on AI Agents).
    const badges = shown.map((v) => (
      <div
        key={v.id}
        className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
        data-tip={`${v.isActive ? 'Active · ' : ''}Created ${formatDateTime(v.createdAt)}`}
      >
        <button
          type="button"
          onClick={() => openVersion(v)}
          disabled={disabled}
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border font-mono text-xs font-bold transition-all disabled:opacity-50 ${
            v.isActive
              ? 'bg-primary/10 border-primary/40 text-primary'
              : 'bg-base-200/50 border-base-content/10 text-base-content/70 hover:border-primary/40 hover:text-primary'
          }`}
        >
          {v.isActive && <CheckCircle2 size={12} />}
          {versionLabel(v)}
        </button>
      </div>
    ));
    if (hiddenCount <= 0) { return badges; }
    return (
      <>
        {badges}
        <button
          type="button"
          onClick={() => setShowAllVersions((s) => !s)}
          aria-expanded={showAllVersions}
          className="px-2 py-1 text-xs font-bold text-primary hover:underline"
        >
          {showAllVersions ? 'Show less' : `Show all (${hiddenCount} more)`}
        </button>
      </>
    );
  };

  const renderViewBody = () => {
    // Prompt, loader and error share one fixed-size frame so Previous / Next don't resize the modal.
    if (viewLoading) {
      return (
        <div className={`${promptFrameClass} flex flex-col items-center justify-center gap-3`} aria-busy="true">
          <div className="w-8 h-8 border-[3px] border-primary/20 border-t-primary rounded-full animate-spin"></div>
          <span className="text-xs font-semibold text-base-content/50">Loading prompt version...</span>
        </div>
      );
    }
    if (viewError) {
      return (
        <div className={`${promptFrameClass} flex items-center justify-center`}>
          <div role="alert" className="alert alert-warning rounded-xl text-xs font-semibold max-w-md">
            <AlertTriangle size={16} className="shrink-0" />
            <span>{viewError}</span>
          </div>
        </div>
      );
    }
    if (confirming) {
      return (
        <p className="text-base-content/70 text-sm leading-relaxed">
          Restore the system prompt to{' '}
          <strong className="text-base-content font-mono px-1.5 py-0.5 rounded bg-base-200">{versionLabel(viewing)}</strong>?
          {showNewVersion
            ? 'This updates the agent immediately and replaces any unsaved changes in the Prompt field.'
            : 'This updates the agent immediately.'}
        </p>
      );
    }
    return (
      <pre className={`${promptFrameClass} overflow-y-auto px-4 py-3 font-mono text-xs text-base-content whitespace-pre-wrap break-words`}>
        {viewing?.prompt || 'This version has no prompt.'}
      </pre>
    );
  };

  const renderViewActions = () => {
    if (viewLoading || viewError) { return null; }
    if (confirming) {
      return (
        <>
          <button type="button" onClick={() => setConfirming(false)} disabled={restoring} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="button" onClick={handleRestore} disabled={restoring} className={primaryButtonClass}>
            {restoring ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                Restoring...
              </>
            ) : (
              <>
                <RotateCcw size={14} />
                Restore
              </>
            )}
          </button>
        </>
      );
    }
    // Restore itself sits next to Previous / Next (see the modal header row).
    return null;
  };

  // Shown for every version; the current one can't be restored onto itself.
  const restoreEnabled = Boolean(viewing) && !viewing.isActive && !viewLoading && !viewError;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <History size={14} className="text-base-content/50" />
        <span className="text-[10px] font-black text-base-content/50 uppercase tracking-widest">Prompt Versions</span>
      </div>
      {error && (
        <div role="alert" className="alert alert-warning rounded-xl text-xs font-semibold">
          <AlertTriangle size={16} className="shrink-0" />
          <span>{error}</span>
          <button type="button" onClick={() => setReloadKey((k) => k + 1)} className="btn btn-xs btn-ghost">
            Retry
          </button>
        </div>
      )}
      {/* No overflow clipping here: it would cut off the tooltips above the badges. */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Edit page only, always first. Selected: save as the next version; otherwise the current
            version is kept. The label itself is worked out when saving. */}
        {showNewVersion && (
          <div
            className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
            data-tip={newVersionSelected ? 'Saves as the next prompt version' : 'Save as the next prompt version'}
          >
            <button
              type="button"
              onClick={() => onNewVersionChange(!newVersionSelected)}
              disabled={disabled}
              aria-pressed={newVersionSelected}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold transition-all disabled:opacity-50 ${
                newVersionSelected
                  ? 'bg-primary border-primary text-primary-content'
                  : 'border-dashed border-primary/50 bg-base-100 text-primary hover:bg-primary/5'
              }`}
            >
              {newVersionSelected ? <CheckCircle2 size={12} /> : <Plus size={12} />}
              New version
            </button>
          </div>
        )}
        {renderVersionBadges()}
      </div>

      <Modal
        isOpen={!!viewing}
        onClose={closeView}
        title={confirming ? 'Restore Prompt Version' : `Prompt version ${versionLabel(viewing)}`}
        className="max-w-2xl w-full"
      >
        <div className="space-y-4">
          {!confirming && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2 text-xs text-base-content/50">
                {viewing?.isActive && <ActiveChip />}
                <span>Created {formatDateTime(viewing?.createdAt)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {versions.length > 1 && viewIndex >= 0 && (
                  <>
                    <button
                      type="button"
                      onClick={() => openVersion(olderVersion)}
                      disabled={!olderVersion}
                      className={secondaryButtonClass}
                      aria-label="Previous (older) version"
                    >
                      <ChevronLeft size={14} />
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={() => openVersion(newerVersion)}
                      disabled={!newerVersion}
                      className={secondaryButtonClass}
                      aria-label="Next (newer) version"
                    >
                      Next
                      <ChevronRight size={14} />
                    </button>
                  </>
                )}
                {/* Shown whenever restoring is allowed; disabled for the current version and while it loads / failed. */}
                {canRestore && (
                  <div
                    className={viewing?.isActive
                      ? 'tooltip tooltip-left tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap'
                      : ''}
                    data-tip={viewing?.isActive ? 'This is already the current version' : undefined}
                  >
                    <button
                      type="button"
                      onClick={() => setConfirming(true)}
                      disabled={!restoreEnabled}
                      className={primaryButtonClass}
                    >
                      <RotateCcw size={14} />
                      Restore
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          {renderViewBody()}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {renderViewActions()}
          </div>
        </div>
      </Modal>
    </div>
  );
};

PromptVersionsPanel.propTypes = {
  agentId: PropTypes.string.isRequired,
  // { accountId } in the platform-wide view, {} otherwise.
  accountParams: PropTypes.object.isRequired,
  // Called with the refreshed agent detail and the restored version after a restore.
  onRestored: PropTypes.func,
  disabled: PropTypes.bool,
  // Whether the "New version" badge is selected (the label is computed on save).
  newVersionSelected: PropTypes.bool,
  // Called with true / false when the badge is toggled; required when `showNewVersion`.
  onNewVersionChange: PropTypes.func,
  // Edit page: shows the "New version" badge. The view page turns it off.
  showNewVersion: PropTypes.bool,
  // False hides Restore (e.g. users without ai_agents.update); versions can still be viewed.
  canRestore: PropTypes.bool,
};

export default PromptVersionsPanel;
